/**
 * Pokemon Sprite Configuration
 *
 * Per-Pokemon scale adjustments to normalize sprite sizes.
 * The scale value multiplies the base size passed to PokemonSprite.
 *
 * scale: 1.0 = use base size as-is
 * scale: 0.5 = 50% smaller (for Pokemon with large sprites)
 * scale: 1.5 = 50% larger (for Pokemon with tiny sprites)
 *
 * Guidelines:
 * - Tiny Pokemon (Joltik, baby Pokemon): 0.5
 * - Small Pokemon (unevolved starters, small rodents): 0.5-0.6
 * - Medium-small Pokemon (first evolutions): 0.65-0.75
 * - Medium Pokemon (most middle evolutions): 0.8-0.9
 * - Large Pokemon (final evolutions, legendaries): 1.0
 * - Very large Pokemon (Wailord, Steelix): 1.0-1.1
 */

export const POKEMON_SPRITE_CONFIG = {
  // Default for Pokemon without specific config
  default: { scale: 1.0 },

  // ===== TINY POKEMON (0.5) =====
  'Joltik': { scale: 0.5 },
  'Azurill': { scale: 0.5 },
  'Pichu': { scale: 0.5 },
  'Togepi': { scale: 0.5 },
  'Igglybuff': { scale: 0.5 },
  'Cleffa': { scale: 0.5 },
  'Clefairy': { scale: 0.55 },
  'Tyrogue': { scale: 0.5 },
  'Smoochum': { scale: 0.5 },
  'Elekid': { scale: 0.5 },
  'Magby': { scale: 0.5 },
  'Wynaut': { scale: 0.5 },
  'Budew': { scale: 0.5 },
  'Chingling': { scale: 0.5 },
  'Bonsly': { scale: 0.5 },
  'Mime Jr.': { scale: 0.5 },
  'Happiny': { scale: 0.5 },
  'Munchlax': { scale: 0.5 },
  'Riolu': { scale: 0.5 },
  'Mantyke': { scale: 0.5 },
  'Tympole': { scale: 0.5 },

  // ===== SMALL POKEMON - Unevolved (0.5-0.55) =====
  'Bulbasaur': { scale: 0.5 },
  'Charmander': { scale: 0.5 },
  'Squirtle': { scale: 0.5 },
  'Caterpie': { scale: 0.5 },
  'Weedle': { scale: 0.5 },
  'Pidgey': { scale: 0.5 },
  'Rattata': { scale: 0.55 },
  'Spearow': { scale: 0.5 },
  'Ekans': { scale: 0.55 },
  'Pikachu': { scale: 0.55 },
  'Sandshrew': { scale: 0.45 },
  'Jigglypuff': { scale: 0.45 },
  'Nidoran♀': { scale: 0.5 },
  'Nidoran♂': { scale: 0.5 },
  'Vulpix': { scale: 0.55 },
  'Zubat': { scale: 0.55 },
  'Oddish': { scale: 0.5 },
  'Paras': { scale: 0.5 },
  'Venonat': { scale: 0.55 },
  'Diglett': { scale: 0.5 },
  'Meowth': { scale: 0.55 },
  'Psyduck': { scale: 0.55 },
  'Mankey': { scale: 0.55 },
  'Growlithe': { scale: 0.6 },
  'Poliwag': { scale: 0.5 },
  'Abra': { scale: 0.55 },
  'Machop': { scale: 0.55 },
  'Bellsprout': { scale: 0.5 },
  'Tentacool': { scale: 0.55 },
  'Geodude': { scale: 0.55 },
  'Ponyta': { scale: 0.6 },
  'Slowpoke': { scale: 0.6 },
  'Magnemite': { scale: 0.5 },
  'Doduo': { scale: 0.6 },
  'Seel': { scale: 0.55 },
  'Grimer': { scale: 0.55 },
  'Shellder': { scale: 0.4 },
  'Gastly': { scale: 0.55 },
  'Drowzee': { scale: 0.6 },
  'Krabby': { scale: 0.55 },
  'Voltorb': { scale: 0.5 },
  'Exeggcute': { scale: 0.5 },
  'Cubone': { scale: 0.55 },
  'Koffing': { scale: 0.6 },
  'Rhyhorn': { scale: 0.65 },
  'Horsea': { scale: 0.5 },
  'Goldeen': { scale: 0.55 },
  'Staryu': { scale: 0.5 },
  'Magikarp': { scale: 0.55 },
  'Eevee': { scale: 0.5 },
  'Omanyte': { scale: 0.55 },
  'Kabuto': { scale: 0.55 },
  'Dratini': { scale: 0.55 },
  'Chikorita': { scale: 0.5 },
  'Cyndaquil': { scale: 0.5 },
  'Totodile': { scale: 0.5 },
  'Sentret': { scale: 0.5 },
  'Hoothoot': { scale: 0.55 },
  'Ledyba': { scale: 0.55 },
  'Spinarak': { scale: 0.5 },
  'Chinchou': { scale: 0.55 },
  'Natu': { scale: 0.5 },
  'Mareep': { scale: 0.55 },
  'Hoppip': { scale: 0.5 },
  'Sunkern': { scale: 0.5 },
  'Wooper': { scale: 0.5 },
  'Pineco': { scale: 0.55 },
  'Snubbull': { scale: 0.55 },
  'Teddiursa': { scale: 0.55 },
  'Slugma': { scale: 0.55 },
  'Swinub': { scale: 0.5 },
  'Corsola': { scale: 0.55 },
  'Remoraid': { scale: 0.5 },
  'Houndour': { scale: 0.55 },
  'Phanpy': { scale: 0.55 },
  'Larvitar': { scale: 0.55 },
  'Treecko': { scale: 0.55 },
  'Torchic': { scale: 0.5 },
  'Mudkip': { scale: 0.5 },
  'Poochyena': { scale: 0.55 },
  'Zigzagoon': { scale: 0.55 },
  'Wurmple': { scale: 0.5 },
  'Lotad': { scale: 0.5 },
  'Seedot': { scale: 0.5 },
  'Taillow': { scale: 0.5 },
  'Wingull': { scale: 0.55 },
  'Ralts': { scale: 0.5 },
  'Surskit': { scale: 0.5 },
  'Shroomish': { scale: 0.5 },
  'Slakoth': { scale: 0.55 },
  'Nincada': { scale: 0.5 },
  'Whismur': { scale: 0.5 },
  'Makuhita': { scale: 0.55 },
  'Nosepass': { scale: 0.55 },
  'Skitty': { scale: 0.55 },
  'Aron': { scale: 0.5 },
  'Meditite': { scale: 0.55 },
  'Electrike': { scale: 0.5 },
  'Plusle': { scale: 0.55 },
  'Minun': { scale: 0.55 },
  'Gulpin': { scale: 0.5 },
  'Numel': { scale: 0.55 },
  'Spoink': { scale: 0.55 },
  'Trapinch': { scale: 0.5 },
  'Cacnea': { scale: 0.55 },
  'Swablu': { scale: 0.55 },
  'Barboach': { scale: 0.5 },
  'Corphish': { scale: 0.55 },
  'Baltoy': { scale: 0.55 },
  'Lileep': { scale: 0.55 },
  'Anorith': { scale: 0.55 },
  'Feebas': { scale: 0.5 },
  'Castform': { scale: 0.5 },
  'Kecleon': { scale: 0.6 },
  'Shuppet': { scale: 0.55 },
  'Duskull': { scale: 0.55 },
  'Snorunt': { scale: 0.55 },
  'Spheal': { scale: 0.55 },
  'Clamperl': { scale: 0.5 },
  'Luvdisc': { scale: 0.5 },
  'Bagon': { scale: 0.55 },
  'Beldum': { scale: 0.5 },
  'Turtwig': { scale: 0.55 },
  'Chimchar': { scale: 0.5 },
  'Piplup': { scale: 0.5 },
  'Starly': { scale: 0.5 },
  'Bidoof': { scale: 0.55 },
  'Kricketot': { scale: 0.5 },
  'Shinx': { scale: 0.55 },
  'Cranidos': { scale: 0.6 },
  'Shieldon': { scale: 0.55 },
  'Burmy': { scale: 0.5 },
  'Combee': { scale: 0.5 },
  'Buizel': { scale: 0.55 },
  'Cherubi': { scale: 0.5 },
  'Shellos': { scale: 0.5 },
  'Drifloon': { scale: 0.55 },
  'Buneary': { scale: 0.55 },
  'Glameow': { scale: 0.55 },
  'Stunky': { scale: 0.55 },
  'Bronzor': { scale: 0.5 },
  'Gible': { scale: 0.55 },
  'Hippopotas': { scale: 0.6 },
  'Skorupi': { scale: 0.55 },
  'Croagunk': { scale: 0.55 },
  'Finneon': { scale: 0.5 },
  'Snover': { scale: 0.6 },
  'Snivy': { scale: 0.5 },
  'Tepig': { scale: 0.5 },
  'Oshawott': { scale: 0.5 },
  'Patrat': { scale: 0.55 },
  'Lillipup': { scale: 0.55 },
  'Purrloin': { scale: 0.55 },
  'Pidove': { scale: 0.5 },
  'Roggenrola': { scale: 0.55 },
  'Woobat': { scale: 0.5 },
  'Drilbur': { scale: 0.55 },
  'Sewaddle': { scale: 0.5 },
  'Venipede': { scale: 0.55 },
  'Cottonee': { scale: 0.5 },
  'Petilil': { scale: 0.5 },
  'Sandile': { scale: 0.55 },
  'Darumaka': { scale: 0.55 },
  'Dwebble': { scale: 0.5 },
  'Scraggy': { scale: 0.55 },
  'Trubbish': { scale: 0.55 },
  'Gothita': { scale: 0.55 },
  'Solosis': { scale: 0.5 },
  'Ducklett': { scale: 0.55 },
  'Vanillite': { scale: 0.5 },
  'Deerling': { scale: 0.55 },
  'Karrablast': { scale: 0.5 },
  'Foongus': { scale: 0.5 },
  'Ferroseed': { scale: 0.55 },
  'Klink': { scale: 0.55 },
  'Tynamo': { scale: 0.5 },
  'Elgyem': { scale: 0.55 },
  'Litwick': { scale: 0.5 },
  'Axew': { scale: 0.55 },
  'Cubchoo': { scale: 0.55 },
  'Shelmet': { scale: 0.5 },
  'Stunfisk': { scale: 0.6 },
  'Mienfoo': { scale: 0.55 },
  'Golett': { scale: 0.6 },
  'Pawniard': { scale: 0.55 },
  'Rufflet': { scale: 0.55 },
  'Vullaby': { scale: 0.55 },
  'Deino': { scale: 0.55 },
  'Larvesta': { scale: 0.55 },
  'Chespin': { scale: 0.5 },
  'Fennekin': { scale: 0.5 },
  'Froakie': { scale: 0.5 },
  'Bunnelby': { scale: 0.55 },
  'Fletchling': { scale: 0.5 },
  'Scatterbug': { scale: 0.5 },
  'Litleo': { scale: 0.55 },
  'Flabebe': { scale: 0.5 },
  'Skiddo': { scale: 0.6 },
  'Pancham': { scale: 0.55 },
  'Espurr': { scale: 0.55 },
  'Honedge': { scale: 0.55 },
  'Spritzee': { scale: 0.5 },
  'Swirlix': { scale: 0.5 },
  'Inkay': { scale: 0.5 },
  'Binacle': { scale: 0.55 },
  'Skrelp': { scale: 0.55 },
  'Clauncher': { scale: 0.55 },
  'Helioptile': { scale: 0.55 },
  'Tyrunt': { scale: 0.6 },
  'Amaura': { scale: 0.6 },
  'Goomy': { scale: 0.5 },
  'Klefki': { scale: 0.5 },
  'Phantump': { scale: 0.55 },
  'Pumpkaboo': { scale: 0.55 },
  'Bergmite': { scale: 0.55 },
  'Noibat': { scale: 0.55 },
  'Rowlet': { scale: 0.5 },
  'Litten': { scale: 0.5 },
  'Popplio': { scale: 0.5 },
  'Yungoos': { scale: 0.55 },
  'Grubbin': { scale: 0.5 },
  'Cutiefly': { scale: 0.5 },
  'Rockruff': { scale: 0.55 },
  'Wishiwashi': { scale: 0.55 },
  'Mareanie': { scale: 0.55 },
  'Mudbray': { scale: 0.6 },
  'Dewpider': { scale: 0.5 },
  'Fomantis': { scale: 0.5 },
  'Morelull': { scale: 0.5 },
  'Salandit': { scale: 0.55 },
  'Stufful': { scale: 0.55 },
  'Bounsweet': { scale: 0.5 },
  'Comfey': { scale: 0.5 },
  'Sandygast': { scale: 0.55 },
  'Pyukumuku': { scale: 0.5 },
  'Togedemaru': { scale: 0.5 },
  'Mimikyu': { scale: 0.55 },
  'Bruxish': { scale: 0.6 },
  'Wimpod': { scale: 0.55 },
  'Jangmo-o': { scale: 0.55 },
  'Wooloo': { scale: 0.55 },
  'Skwovet': { scale: 0.55 },
  'Nickit': { scale: 0.55 },
  'Gossifleur': { scale: 0.5 },
  'Chewtle': { scale: 0.55 },
  'Yamper': { scale: 0.55 },
  'Rolycoly': { scale: 0.55 },
  'Applin': { scale: 0.5 },
  'Silicobra': { scale: 0.55 },
  'Cramorant': { scale: 0.6 },
  'Arrokuda': { scale: 0.5 },
  'Toxel': { scale: 0.5 },
  'Sizzlipede': { scale: 0.55 },
  'Clobbopus': { scale: 0.55 },
  'Sinistea': { scale: 0.5 },
  'Hatenna': { scale: 0.5 },
  'Impidimp': { scale: 0.55 },
  'Milcery': { scale: 0.5 },
  'Falinks': { scale: 0.6 },
  'Pincurchin': { scale: 0.5 },
  'Snom': { scale: 0.5 },
  'Cufant': { scale: 0.6 },
  'Dreepy': { scale: 0.5 },
  'Sobble': { scale: 0.5 },
  'Pawmi': { scale: 0.5 },
  'Tinkatink': { scale: 0.55 },
  'Zorua': { scale: 0.55 },
  'Tadbulb': { scale: 0.5 },
  'Carvanha': { scale: 0.55 },
  'Maschiff': { scale: 0.55 },

  // ===== MEDIUM-SMALL POKEMON - First Evolutions (0.6-0.7) =====
  'Ivysaur': { scale: 0.65 },
  'Charmeleon': { scale: 0.65 },
  'Wartortle': { scale: 0.65 },
  'Metapod': { scale: 0.55 },
  'Kakuna': { scale: 0.55 },
  'Pidgeotto': { scale: 0.7 },
  'Raticate': { scale: 0.65 },
  'Fearow': { scale: 0.75 },
  'Arbok': { scale: 0.8 },
  'Raichu': { scale: 0.7 },
  'Sandslash': { scale: 0.7 },
  'Nidorina': { scale: 0.65 },
  'Nidorino': { scale: 0.65 },
  'Clefable': { scale: 0.7 },
  'Wigglytuff': { scale: 0.6 },
  'Ninetales': { scale: 0.8 },
  'Golbat': { scale: 0.75 },
  'Gloom': { scale: 0.65 },
  'Parasect': { scale: 0.7 },
  'Venomoth': { scale: 0.75 },
  'Dugtrio': { scale: 0.7 },
  'Persian': { scale: 0.75 },
  'Golduck': { scale: 0.8 },
  'Primeape': { scale: 0.75 },
  'Poliwhirl': { scale: 0.7 },
  'Kadabra': { scale: 0.75 },
  'Machoke': { scale: 0.8 },
  'Weepinbell': { scale: 0.65 },
  'Tentacruel': { scale: 0.85 },
  'Graveler': { scale: 0.7 },
  'Rapidash': { scale: 0.85 },
  'Slowbro': { scale: 0.8 },
  'Magneton': { scale: 0.7 },
  'Dodrio': { scale: 0.8 },
  'Dewgong': { scale: 0.8 },
  'Muk': { scale: 0.85 },
  'Cloyster': { scale: 0.8 },
  'Haunter': { scale: 0.7 },
  'Hypno': { scale: 0.8 },
  'Kingler': { scale: 0.8 },
  'Electrode': { scale: 0.65 },
  'Exeggutor': { scale: 0.9 },
  'Marowak': { scale: 0.75 },
  'Weezing': { scale: 0.8 },
  'Rhydon': { scale: 0.9 },
  'Seadra': { scale: 0.7 },
  'Seaking': { scale: 0.75 },
  'Starmie': { scale: 0.75 },
  'Jynx': { scale: 0.8 },
  'Electabuzz': { scale: 0.85 },
  'Magmar': { scale: 0.85 },
  'Tauros': { scale: 0.85 },
  'Gyarados': { scale: 1.0 },
  'Lapras': { scale: 0.95 },
  'Ditto': { scale: 0.5 },
  'Vaporeon': { scale: 0.8 },
  'Jolteon': { scale: 0.75 },
  'Flareon': { scale: 0.75 },
  'Porygon': { scale: 0.6 },
  'Omastar': { scale: 0.8 },
  'Kabutops': { scale: 0.85 },
  'Snorlax': { scale: 1.0 },
  'Dragonair': { scale: 0.85 },
  'Bayleef': { scale: 0.7 },
  'Quilava': { scale: 0.65 },
  'Croconaw': { scale: 0.7 },
  'Furret': { scale: 0.7 },
  'Noctowl': { scale: 0.8 },
  'Ledian': { scale: 0.7 },
  'Ariados': { scale: 0.75 },
  'Crobat': { scale: 0.85 },
  'Lanturn': { scale: 0.8 },
  'Xatu': { scale: 0.75 },
  'Flaaffy': { scale: 0.7 },
  'Marill': { scale: 0.55 },
  'Azumarill': { scale: 0.75 },
  'Skiploom': { scale: 0.6 },
  'Jumpluff': { scale: 0.7 },
  'Sunflora': { scale: 0.7 },
  'Quagsire': { scale: 0.8 },
  'Espeon': { scale: 0.75 },
  'Umbreon': { scale: 0.75 },
  'Murkrow': { scale: 0.6 },
  'Slowking': { scale: 0.85 },
  'Misdreavus': { scale: 0.6 },
  'Girafarig': { scale: 0.8 },
  'Forretress': { scale: 0.75 },
  'Dunsparce': { scale: 0.7 },
  'Gligar': { scale: 0.7 },
  'Granbull': { scale: 0.75 },
  'Qwilfish': { scale: 0.6 },
  'Scizor': { scale: 0.9 },
  'Shuckle': { scale: 0.55 },
  'Heracross': { scale: 0.85 },
  'Sneasel': { scale: 0.7 },
  'Ursaring': { scale: 0.95 },
  'Magcargo': { scale: 0.7 },
  'Piloswine': { scale: 0.85 },
  'Octillery': { scale: 0.7 },
  'Delibird': { scale: 0.65 },
  'Mantine': { scale: 0.9 },
  'Houndoom': { scale: 0.85 },
  'Kingdra': { scale: 0.85 },
  'Donphan': { scale: 0.85 },
  'Porygon2': { scale: 0.65 },
  'Stantler': { scale: 0.85 },
  'Hitmontop': { scale: 0.7 },
  'Miltank': { scale: 0.85 },
  'Blissey': { scale: 0.85 },
  'Pupitar': { scale: 0.7 },
  'Grovyle': { scale: 0.7 },
  'Combusken': { scale: 0.75 },
  'Marshtomp': { scale: 0.7 },
  'Mightyena': { scale: 0.8 },
  'Linoone': { scale: 0.7 },
  'Silcoon': { scale: 0.55 },
  'Cascoon': { scale: 0.55 },
  'Lombre': { scale: 0.65 },
  'Nuzleaf': { scale: 0.65 },
  'Swellow': { scale: 0.75 },
  'Pelipper': { scale: 0.8 },
  'Kirlia': { scale: 0.65 },
  'Masquerain': { scale: 0.7 },
  'Breloom': { scale: 0.8 },
  'Vigoroth': { scale: 0.8 },
  'Ninjask': { scale: 0.7 },
  'Shedinja': { scale: 0.6 },
  'Loudred': { scale: 0.75 },
  'Hariyama': { scale: 0.95 },
  'Delcatty': { scale: 0.7 },
  'Lairon': { scale: 0.75 },
  'Medicham': { scale: 0.8 },
  'Manectric': { scale: 0.8 },
  'Volbeat': { scale: 0.65 },
  'Illumise': { scale: 0.65 },
  'Roselia': { scale: 0.65 },
  'Swalot': { scale: 0.75 },
  'Camerupt': { scale: 0.9 },
  'Grumpig': { scale: 0.8 },
  'Vibrava': { scale: 0.7 },
  'Cacturne': { scale: 0.85 },
  'Altaria': { scale: 0.85 },
  'Zangoose': { scale: 0.8 },
  'Seviper': { scale: 0.85 },
  'Lunatone': { scale: 0.7 },
  'Solrock': { scale: 0.7 },
  'Whiscash': { scale: 0.8 },
  'Crawdaunt': { scale: 0.85 },
  'Claydol': { scale: 0.8 },
  'Cradily': { scale: 0.85 },
  'Armaldo': { scale: 0.9 },
  'Milotic': { scale: 0.95 },
  'Banette': { scale: 0.75 },
  'Dusclops': { scale: 0.8 },
  'Tropius': { scale: 0.95 },
  'Chimecho': { scale: 0.6 },
  'Absol': { scale: 0.85 },
  'Glalie': { scale: 0.75 },
  'Sealeo': { scale: 0.75 },
  'Walrein': { scale: 0.95 },
  'Huntail': { scale: 0.8 },
  'Gorebyss': { scale: 0.8 },
  'Relicanth': { scale: 0.8 },
  'Shelgon': { scale: 0.75 },
  'Metang': { scale: 0.75 },
  'Grotle': { scale: 0.75 },
  'Monferno': { scale: 0.7 },
  'Prinplup': { scale: 0.7 },
  'Staravia': { scale: 0.65 },
  'Bibarel': { scale: 0.75 },
  'Kricketune': { scale: 0.7 },
  'Luxio': { scale: 0.7 },
  'Rampardos': { scale: 0.9 },
  'Bastiodon': { scale: 0.9 },
  'Wormadam': { scale: 0.65 },
  'Mothim': { scale: 0.7 },
  'Vespiquen': { scale: 0.85 },
  'Floatzel': { scale: 0.85 },
  'Cherrim': { scale: 0.6 },
  'Gastrodon': { scale: 0.75 },
  'Drifblim': { scale: 0.85 },
  'Lopunny': { scale: 0.85 },
  'Mismagius': { scale: 0.8 },
  'Honchkrow': { scale: 0.85 },
  'Purugly': { scale: 0.8 },
  'Skuntank': { scale: 0.85 },
  'Bronzong': { scale: 0.85 },
  'Gabite': { scale: 0.8 },
  'Hippowdon': { scale: 0.95 },
  'Drapion': { scale: 0.9 },
  'Toxicroak': { scale: 0.85 },
  'Carnivine': { scale: 0.75 },
  'Lumineon': { scale: 0.7 },
  'Abomasnow': { scale: 0.95 },
  'Weavile': { scale: 0.8 },
  'Magnezone': { scale: 0.85 },
  'Lickilicky': { scale: 0.9 },
  'Rhyperior': { scale: 1.0 },
  'Tangrowth': { scale: 0.95 },
  'Electivire': { scale: 0.95 },
  'Magmortar': { scale: 0.95 },
  'Togekiss': { scale: 0.85 },
  'Yanmega': { scale: 0.85 },
  'Leafeon': { scale: 0.75 },
  'Glaceon': { scale: 0.75 },
  'Gliscor': { scale: 0.85 },
  'Mamoswine': { scale: 1.0 },
  'Porygon-Z': { scale: 0.8 },
  'Gallade': { scale: 0.9 },
  'Probopass': { scale: 0.8 },
  'Dusknoir': { scale: 0.9 },
  'Froslass': { scale: 0.75 },
  'Togetic': { scale: 0.65 },
  'Yanma': { scale: 0.65 },
  'Blitzle': { scale: 0.6 },
  'Torkoal': { scale: 0.75 },
  'Sawk': { scale: 0.8 },
  'Throh': { scale: 0.8 },
  'Basculin': { scale: 0.7 },
  'Farfetchd': { scale: 0.7 },
  'Servine': { scale: 0.7 },
  'Pignite': { scale: 0.75 },
  'Dewott': { scale: 0.7 },
  'Watchog': { scale: 0.75 },
  'Herdier': { scale: 0.7 },
  'Liepard': { scale: 0.8 },
  'Tranquill': { scale: 0.7 },
  'Boldore': { scale: 0.75 },
  'Swoobat': { scale: 0.7 },
  'Excadrill': { scale: 0.85 },
  'Swadloon': { scale: 0.6 },
  'Whirlipede': { scale: 0.65 },
  'Scolipede': { scale: 0.95 },
  'Whimsicott': { scale: 0.7 },
  'Lilligant': { scale: 0.8 },
  'Krokorok': { scale: 0.75 },
  'Darmanitan': { scale: 0.9 },
  'Maractus': { scale: 0.75 },
  'Crustle': { scale: 0.8 },
  'Scrafty': { scale: 0.8 },
  'Sigilyph': { scale: 0.8 },
  'Cofagrigus': { scale: 0.9 },
  'Tirtouga': { scale: 0.7 },
  'Carracosta': { scale: 0.9 },
  'Archen': { scale: 0.6 },
  'Archeops': { scale: 0.9 },
  'Garbodor': { scale: 0.95 },
  'Zoroark': { scale: 0.9 },
  'Gothorita': { scale: 0.65 },
  'Gothitelle': { scale: 0.85 },
  'Duosion': { scale: 0.65 },
  'Reuniclus': { scale: 0.8 },
  'Swanna': { scale: 0.8 },
  'Vanillish': { scale: 0.6 },
  'Vanilluxe': { scale: 0.8 },
  'Sawsbuck': { scale: 0.9 },
  'Escavalier': { scale: 0.8 },
  'Amoonguss': { scale: 0.8 },
  'Galvantula': { scale: 0.8 },
  'Ferrothorn': { scale: 0.8 },
  'Klang': { scale: 0.65 },
  'Klinklang': { scale: 0.8 },
  'Eelektrik': { scale: 0.75 },
  'Eelektross': { scale: 0.9 },
  'Beheeyem': { scale: 0.75 },
  'Lampent': { scale: 0.65 },
  'Chandelure': { scale: 0.85 },
  'Fraxure': { scale: 0.75 },
  'Haxorus': { scale: 0.95 },
  'Beartic': { scale: 0.95 },
  'Accelgor': { scale: 0.75 },
  'Mienshao': { scale: 0.85 },
  'Druddigon': { scale: 0.9 },
  'Golurk': { scale: 1.0 },
  'Bisharp': { scale: 0.85 },
  'Braviary': { scale: 0.9 },
  'Mandibuzz': { scale: 0.9 },
  'Heatmor': { scale: 0.85 },
  'Durant': { scale: 0.65 },
  'Zweilous': { scale: 0.8 },
  'Hydreigon': { scale: 0.95 },
  'Volcarona': { scale: 0.9 },
  'Quilladin': { scale: 0.7 },
  'Braixen': { scale: 0.75 },
  'Frogadier': { scale: 0.7 },
  'Diggersby': { scale: 0.85 },
  'Fletchinder': { scale: 0.7 },
  'Spewpa': { scale: 0.55 },
  'Vivillon': { scale: 0.7 },
  'Pyroar': { scale: 0.9 },
  'Floette': { scale: 0.55 },
  'Florges': { scale: 0.85 },
  'Gogoat': { scale: 0.95 },
  'Pangoro': { scale: 0.95 },
  'Meowstic': { scale: 0.75 },
  'Doublade': { scale: 0.7 },
  'Aegislash': { scale: 0.85 },
  'Aromatisse': { scale: 0.75 },
  'Slurpuff': { scale: 0.7 },
  'Malamar': { scale: 0.85 },
  'Barbaracle': { scale: 0.9 },
  'Dragalge': { scale: 0.85 },
  'Clawitzer': { scale: 0.8 },
  'Heliolisk': { scale: 0.8 },
  'Tyrantrum': { scale: 1.0 },
  'Aurorus': { scale: 1.0 },
  'Sylveon': { scale: 0.8 },
  'Hawlucha': { scale: 0.75 },
  'Dedenne': { scale: 0.5 },
  'Carbink': { scale: 0.55 },
  'Sliggoo': { scale: 0.7 },
  'Goodra': { scale: 0.95 },
  'Trevenant': { scale: 0.9 },
  'Gourgeist': { scale: 0.75 },
  'Avalugg': { scale: 0.95 },
  'Noivern': { scale: 0.9 },
  'Dartrix': { scale: 0.7 },
  'Torracat': { scale: 0.7 },
  'Brionne': { scale: 0.7 },
  'Gumshoos': { scale: 0.75 },
  'Greedent': { scale: 0.75 },
  'Dubwool': { scale: 0.85 },
  'Thievul': { scale: 0.8 },
  'Eldegoss': { scale: 0.7 },
  'Drednaw': { scale: 0.9 },
  'Boltund': { scale: 0.8 },
  'Carkol': { scale: 0.75 },
  'Coalossal': { scale: 1.0 },
  'Flapple': { scale: 0.7 },
  'Appletun': { scale: 0.75 },
  'Sandaconda': { scale: 0.85 },
  'Barraskewda': { scale: 0.75 },
  'Toxtricity': { scale: 0.9 },
  'Centiskorch': { scale: 0.95 },
  'Grapploct': { scale: 0.85 },
  'Polteageist': { scale: 0.6 },
  'Hattrem': { scale: 0.6 },
  'Hatterene': { scale: 0.9 },
  'Morgrem': { scale: 0.7 },
  'Grimmsnarl': { scale: 0.95 },
  'Obstagoon': { scale: 0.95 },
  'Perrserker': { scale: 0.8 },
  'Cursola': { scale: 0.75 },
  'Sirfetchd': { scale: 0.8 },
  'Mr. Rime': { scale: 0.85 },
  'Runerigus': { scale: 0.85 },
  'Alcremie': { scale: 0.65 },
  'Frosmoth': { scale: 0.85 },
  'Stonjourner': { scale: 0.9 },
  'Eiscue': { scale: 0.7 },
  'Indeedee': { scale: 0.75 },
  'Morpeko': { scale: 0.55 },
  'Copperajah': { scale: 1.0 },
  'Dracozolt': { scale: 0.9 },
  'Arctozolt': { scale: 0.9 },
  'Dracovish': { scale: 0.9 },
  'Arctovish': { scale: 0.9 },
  'Duraludon': { scale: 0.95 },
  'Drakloak': { scale: 0.75 },
  'Dragapult': { scale: 0.95 },

  // ===== LARGE POKEMON - Final Evolutions (0.85-1.0) =====
  'Venusaur': { scale: 0.95 },
  'Charizard': { scale: 0.95 },
  'Blastoise': { scale: 0.95 },
  'Butterfree': { scale: 0.8 },
  'Beedrill': { scale: 0.8 },
  'Pidgeot': { scale: 0.9 },
  'Nidoqueen': { scale: 0.9 },
  'Nidoking': { scale: 0.9 },
  'Arcanine': { scale: 0.95 },
  'Vileplume': { scale: 0.85 },
  'Victreebel': { scale: 0.85 },
  'Golem': { scale: 0.9 },
  'Alakazam': { scale: 0.9 },
  'Machamp': { scale: 0.95 },
  'Gengar': { scale: 0.85 },
  'Onix': { scale: 0.95 },
  'Hitmonlee': { scale: 0.85 },
  'Hitmonchan': { scale: 0.85 },
  'Lickitung': { scale: 0.85 },
  'Chansey': { scale: 0.8 },
  'Tangela': { scale: 0.75 },
  'Kangaskhan': { scale: 0.95 },
  'Mr. Mime': { scale: 0.8 },
  'Scyther': { scale: 0.9 },
  'Pinsir': { scale: 0.85 },
  'Aerodactyl': { scale: 0.9 },
  'Dragonite': { scale: 1.0 },
  'Meganium': { scale: 0.95 },
  'Typhlosion': { scale: 0.9 },
  'Feraligatr': { scale: 0.95 },
  'Ampharos': { scale: 0.9 },
  'Bellossom': { scale: 0.7 },
  'Politoed': { scale: 0.8 },
  'Sudowoodo': { scale: 0.85 },
  'Wobbuffet': { scale: 0.85 },
  'Sceptile': { scale: 0.95 },
  'Blaziken': { scale: 0.95 },
  'Swampert': { scale: 0.95 },
  'Ludicolo': { scale: 0.9 },
  'Shiftry': { scale: 0.85 },
  'Gardevoir': { scale: 0.9 },
  'Slaking': { scale: 1.0 },
  'Exploud': { scale: 0.95 },
  'Aggron': { scale: 1.0 },
  'Flygon': { scale: 0.9 },
  'Salamence': { scale: 1.0 },
  'Metagross': { scale: 0.95 },
  'Torterra': { scale: 1.0 },
  'Infernape': { scale: 0.9 },
  'Empoleon': { scale: 0.95 },
  'Staraptor': { scale: 0.85 },
  'Luxray': { scale: 0.9 },
  'Roserade': { scale: 0.8 },
  'Garchomp': { scale: 1.0 },
  'Lucario': { scale: 0.85 },
  'Serperior': { scale: 0.95 },
  'Emboar': { scale: 1.0 },
  'Samurott': { scale: 0.95 },
  'Stoutland': { scale: 0.9 },
  'Unfezant': { scale: 0.85 },
  'Gigalith': { scale: 0.95 },
  'Conkeldurr': { scale: 1.0 },
  'Leavanny': { scale: 0.85 },
  'Krookodile': { scale: 0.95 },
  'Chesnaught': { scale: 1.0 },
  'Delphox': { scale: 0.9 },
  'Greninja': { scale: 0.9 },
  'Talonflame': { scale: 0.85 },
  'Decidueye': { scale: 0.95 },
  'Incineroar': { scale: 0.95 },
  'Primarina': { scale: 0.95 },
  'Rillaboom': { scale: 1.0 },
  'Cinderace': { scale: 0.9 },
  'Inteleon': { scale: 0.95 },
  'Corviknight': { scale: 0.95 },

  // ===== LEGENDARY/MYTHICAL POKEMON (0.9-1.1) =====
  'Articuno': { scale: 0.95 },
  'Zapdos': { scale: 0.95 },
  'Moltres': { scale: 0.95 },
  'Mewtwo': { scale: 1.0 },
  'Mew': { scale: 0.6 },
  'Raikou': { scale: 0.95 },
  'Entei': { scale: 0.95 },
  'Suicune': { scale: 0.95 },
  'Lugia': { scale: 1.0 },
  'HoOh': { scale: 1.0 },
  'Ho-Oh': { scale: 1.0 },
  'Celebi': { scale: 0.6 },
  'Regirock': { scale: 0.9 },
  'Regice': { scale: 0.9 },
  'Registeel': { scale: 0.9 },
  'Latias': { scale: 0.9 },
  'Latios': { scale: 0.9 },
  'Kyogre': { scale: 1.0 },
  'Groudon': { scale: 1.0 },
  'Rayquaza': { scale: 1.1 },
  'Jirachi': { scale: 0.55 },
  'Deoxys': { scale: 0.9 },
  'Uxie': { scale: 0.6 },
  'Mesprit': { scale: 0.6 },
  'Azelf': { scale: 0.6 },
  'Dialga': { scale: 1.0 },
  'Palkia': { scale: 1.0 },
  'Heatran': { scale: 0.95 },
  'Regigigas': { scale: 1.1 },
  'Giratina': { scale: 1.1 },
  'Cresselia': { scale: 0.9 },
  'Phione': { scale: 0.55 },
  'Manaphy': { scale: 0.55 },
  'Darkrai': { scale: 0.9 },
  'Shaymin': { scale: 0.55 },
  'Arceus': { scale: 1.0 },
  'Victini': { scale: 0.55 },
  'Cobalion': { scale: 0.95 },
  'Terrakion': { scale: 0.95 },
  'Virizion': { scale: 0.95 },
  'Tornadus': { scale: 0.9 },
  'Thundurus': { scale: 0.9 },
  'Reshiram': { scale: 1.0 },
  'Zekrom': { scale: 1.0 },
  'Landorus': { scale: 0.9 },
  'Kyurem': { scale: 1.0 },
  'Keldeo': { scale: 0.7 },
  'Meloetta': { scale: 0.7 },
  'Genesect': { scale: 0.9 },
  'Xerneas': { scale: 1.0 },
  'Yveltal': { scale: 1.0 },
  'Zygarde': { scale: 1.0 },
  'Diancie': { scale: 0.65 },
  'Hoopa': { scale: 0.7 },
  'Volcanion': { scale: 0.95 },
  'Tapu Koko': { scale: 0.85 },
  'Tapu Lele': { scale: 0.85 },
  'Tapu Bulu': { scale: 0.9 },
  'Tapu Fini': { scale: 0.85 },
  'Cosmog': { scale: 0.5 },
  'Cosmoem': { scale: 0.55 },
  'Solgaleo': { scale: 1.0 },
  'Lunala': { scale: 1.0 },
  'Nihilego': { scale: 0.85 },
  'Buzzwole': { scale: 0.95 },
  'Pheromosa': { scale: 0.9 },
  'Xurkitree': { scale: 0.95 },
  'Celesteela': { scale: 1.0 },
  'Kartana': { scale: 0.6 },
  'Guzzlord': { scale: 1.0 },
  'Necrozma': { scale: 0.95 },
  'Magearna': { scale: 0.8 },
  'Marshadow': { scale: 0.65 },
  'Poipole': { scale: 0.6 },
  'Naganadel': { scale: 0.95 },
  'Stakataka': { scale: 0.95 },
  'Blacephalon': { scale: 0.9 },
  'Zeraora': { scale: 0.9 },
  'Meltan': { scale: 0.5 },
  'Melmetal': { scale: 1.0 },
  'Zacian': { scale: 0.95 },
  'Zamazenta': { scale: 0.95 },
  'Eternatus': { scale: 1.1 },
  'Kubfu': { scale: 0.6 },
  'Urshifu': { scale: 0.95 },
  'Zarude': { scale: 0.9 },
  'Regieleki': { scale: 0.85 },
  'Regidrago': { scale: 0.9 },
  'Glastrier': { scale: 0.95 },
  'Spectrier': { scale: 0.95 },
  'Calyrex': { scale: 0.85 },

  // ===== VERY LARGE POKEMON (1.0-1.1) =====
  'Wailmer': { scale: 0.7 },
  'Wailord': { scale: 1.0 },
  'Steelix': { scale: 1.0 },
  'Tyranitar': { scale: 1.0 },
};

/**
 * Get the sprite scale for a Pokemon
 * @param {string} pokemonName - The Pokemon name
 * @returns {number} The scale multiplier
 */
export const getPokemonSpriteScale = (pokemonName) => {
  if (!pokemonName) return 1.0;
  const config = POKEMON_SPRITE_CONFIG[pokemonName];
  return config?.scale ?? POKEMON_SPRITE_CONFIG.default.scale;
};
